import {
  IconHeart,
  IconHexagonalPyramid,
  IconShare,
  IconUser,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  getMarketplaceInstallTypeLabel,
  getMarketplaceInstallTypeMeta,
} from "#app/components/marketplace/install-type-badge";
import { Badge } from "#app/components/ui/badge";
import { Button } from "#app/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "#app/components/ui/card";
import { SimpleTooltip } from "#app/components/ui/tooltip";
import { cn } from "#app/utils/misc.tsx";
import {
  extractServiceIcons,
  type ServiceIcon,
} from "#app/utils/service-icons";

/**
 * Operator levels get the same treatment the design gives status badges
 * elsewhere: a tinted pill, one hue per level. The old card encoded the level as
 * a full-card radial gradient (`cardVariants.internBg` and friends), which meant
 * four subtly different card surfaces in one grid and no way to read the level
 * without knowing the code.
 */
const OPERATOR_LEVEL_ACCENTS: Record<string, string> = {
  intern:
    "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-900/30 dark:text-sky-300",
  professional:
    "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 dark:border-fuchsia-900 dark:bg-fuchsia-900/30 dark:text-fuchsia-300",
  manager:
    "border-lime-200 bg-lime-50 text-lime-700 dark:border-lime-900 dark:bg-lime-900/30 dark:text-lime-300",
  director:
    "border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-900 dark:bg-teal-900/30 dark:text-teal-300",
};

export type MarketplaceListingCardData = {
  id: string;
  slug: string;
  title: string;
  description: string;
  /** The listing's own artwork, else the operator-level glyph. Always set. */
  iconUrl: string;
  /** Raw level value (`intern` … `director`), or null when the listing omits it. */
  operatorLevel: string | null;
  /** Localised level name, shown under the title. */
  operatorLevelLabel: string;
  installType: string | null;
  /** Category, subcategory and tags, already localised and deduped. */
  tags: string[];
  /** Integration / service ids used to resolve footer icons client-side. */
  services?: string[];
  favorites: number;
  onboarded: number;
  publisher: {
    /** Org display name or publisher key — the avatar's tooltip. */
    name: string;
    imageUrl: string | null;
    initials: string;
  };
};

type MarketplaceListingCardProps = {
  listing: MarketplaceListingCardData;
  onOpen: () => void;
  /** Omitted on the public marketing route, which has nothing to run against. */
  onTry?: () => void;
  onShareLink?: () => void;
};

/** Beyond this the pills wrap to a third line and the cards lose their common height. */
const MAX_VISIBLE_TAGS = 2;
const MAX_VISIBLE_ICONS = 3;

function collectIntegrationServiceNames(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (typeof item === "string" && item.trim()) return [item.trim()];
    if (item && typeof item === "object") {
      const name = (item as { name?: unknown }).name;
      if (typeof name === "string" && name.trim()) return [name.trim()];
    }
    return [];
  });
}

function servicesFromWorkflowSnapshot(snapshot: unknown): string[] {
  if (!snapshot || typeof snapshot !== "object") return [];
  const names = new Set<string>();
  const add = (value: unknown) => {
    collectIntegrationServiceNames(value).forEach((name) => names.add(name));
  };

  const candidates = [
    snapshot as {
      services?: unknown;
      steps?: unknown;
      workflow?: { services?: unknown; steps?: unknown };
      workflowVersion?: { services?: unknown; steps?: unknown };
    },
  ];
  const root = candidates[0]!;
  add(root.services);
  add(root.workflow?.services);
  add(root.workflowVersion?.services);

  const stepSources = [
    root.steps,
    root.workflow?.steps,
    root.workflowVersion?.steps,
  ];
  for (const steps of stepSources) {
    if (!steps) continue;
    const list = Array.isArray(steps) ? steps : Object.values(steps);
    for (const step of list) {
      add((step as { services?: unknown } | null)?.services);
    }
  }

  return Array.from(names);
}

/** Pull required + optional integration names from a marketplace version. */
export function marketplaceVersionServiceNames(
  version:
    | {
        requiredIntegrations?: unknown;
        optionalIntegrations?: unknown;
        sourceWorkflowSnapshot?: unknown;
      }
    | null
    | undefined,
): string[] {
  if (!version) return [];
  const fromIntegrations = [
    ...collectIntegrationServiceNames(version.requiredIntegrations),
    ...collectIntegrationServiceNames(version.optionalIntegrations),
  ];
  const fromSnapshot = servicesFromWorkflowSnapshot(
    version.sourceWorkflowSnapshot,
  );
  return [...new Set([...fromIntegrations, ...fromSnapshot])];
}

/**
 * A marketplace listing, in the new design's card language — the same frame as
 * `OperatorCard` and `MarketplaceInstallationCard`: a `rounded-3xl` white card
 * with a badge header, a clickable body, and a footer rule under it.
 *
 * Shared by the in-app catalog (`/u/marketplace`) and the public one
 * (`/marketplace`); the only difference between them is whether `onTry` is
 * passed, so the routes map their loader rows into
 * `MarketplaceListingCardData` and this file owns the rest.
 */
export function MarketplaceListingCard({
  listing,
  onOpen,
  onTry,
  onShareLink,
}: MarketplaceListingCardProps) {
  const { t } = useTranslation(["marketing", "listings"]);
  const { t: tListings } = useTranslation("listings");
  const [icons, setIcons] = useState<ServiceIcon[]>([]);

  const installMeta = getMarketplaceInstallTypeMeta(listing.installType);
  const levelAccent = listing.operatorLevel
    ? OPERATOR_LEVEL_ACCENTS[listing.operatorLevel]
    : undefined;

  // Same client-only icon lookup as My Operators / my-listings (localStorage index).
  // Letter-fallback chips stay card-local so detail pages never get empty `<img>`s.
  const servicesKey = (listing.services ?? []).join("\0");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const services = servicesKey ? servicesKey.split("\0") : [];
    if (services.length === 0) {
      setIcons([]);
      return;
    }

    const resolved = extractServiceIcons({ services });
    if (resolved.length > 0) {
      setIcons(resolved);
      return;
    }

    const formatServiceName = (service: string) =>
      service
        .split("/")
        .map((part) =>
          part
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
        )
        .join(" / ");

    setIcons(
      services.slice(0, MAX_VISIBLE_ICONS).map((service) => ({
        iconUrl: "",
        serviceName: formatServiceName(service),
      })),
    );
  }, [servicesKey]);

  return (
    <Card className="gap-0 rounded-3xl border border-zinc-200 from-blue-50 to-white dark:from-zinc-900 dark:to-zinc-900 dark:border-zinc-800 p-0 shadow-none! ring-0! transition-all duration-300 ease-out hover:-translate-y-0.5 hover:bg-linear-to-bl hover:shadow-xs dark:bg-zinc-800/40">
      <CardHeader className=" rounded-t-none flex items-center justify-between gap-0 py-3 pr-3!">
        <CardTitle className="flex min-w-0 flex-wrap items-center gap-2">
          <div className="mp-badges space-x-2">
            {installMeta ? (
              <Badge
                className={cn(
                  "gap-1.5 border px-3 py-3 font-medium",
                  installMeta.badgeClassName,
                )}
              >
                {getMarketplaceInstallTypeLabel(listing.installType, tListings)}
              </Badge>
            ) : null}

            {levelAccent ? (
              <Badge
                className={cn("border px-3 py-3 font-medium", levelAccent)}
              >
                {listing.operatorLevelLabel}
              </Badge>
            ) : null}
          </div>
        </CardTitle>

        <CardAction>
          <div className="flex min-h-8 items-center -space-x-2">
            {icons.slice(0, MAX_VISIBLE_ICONS).map((icon, index) => (
              <span
                key={`${icon.serviceName}-${index}`}
                title={icon.serviceName}
                className="flex h-8 w-8 items-center justify-center rounded-full border-4 dark:border border-white dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-700"
              >
                {icon.iconUrl ? (
                  <img
                    src={icon.iconUrl}
                    alt=""
                    className="h-4 w-4 object-contain"
                  />
                ) : (
                  <span className="text-[10px] font-semibold text-zinc-500">
                    {icon.serviceName.charAt(0).toUpperCase()}
                  </span>
                )}
              </span>
            ))}

            {icons.length > MAX_VISIBLE_ICONS ? (
              <span className="flex h-8 w-8 items-center justify-center rounded-full border-4 border-white bg-zinc-100 text-sm font-medium text-primary dark:border-zinc-900 dark:bg-zinc-800">
                +{icons.length - MAX_VISIBLE_ICONS}
              </span>
            ) : null}
          </div>
        </CardAction>
      </CardHeader>

      <button
        type="button"
        onClick={onOpen}
        className="flex flex-1 cursor-pointer flex-col text-left"
      >
        <CardContent className="flex-1 shrink-0 space-y-3 border-t border-zinc-100 py-4 dark:border-zinc-800/30">
          <h2 className="truncate text-[15px] font-medium text-primary">
            {listing.title}
          </h2>
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 text-[13px] text-muted-foreground">
                {listing.description}
              </p>
            </div>
          </div>

          {listing.tags.length > 0 ? (
            <div className="flex min-w-0 items-center -space-x-2 -ml-2">
              {listing.tags.slice(0, MAX_VISIBLE_TAGS).map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="bg-zinc-50 text-primary font-medium p-2 px-3 h-auto truncate border-2 border-white dark:border-zinc-900 dark:bg-zinc-800 dark:text-zinc-200"
                >
                  {tag}
                </Badge>
              ))}

              {listing.tags.length > MAX_VISIBLE_TAGS ? (
                <span className="text-xs bg-zinc-50 text-primary font-semibold ml-3 p-2 rounded-full border-white dark:border dark:border-zinc-800 dark:bg-zinc-800">
                  +{listing.tags.length - MAX_VISIBLE_TAGS}
                </span>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </button>

      <CardFooter className="flex items-center justify-between border-t border-zinc-100 dark:border-zinc-800/30 py-3! bg-zinc-50 dark:bg-zinc-800/30 rounded-b-none">
        <div className="left-try-btn">
          {onTry ? (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs uppercase text-[#5D69AB] font-semibold dark:bg-accent"
              onClick={onTry}
            >
              {t("marketing:tryButton")}
              <IconHexagonalPyramid className="size-3.5" />
            </Button>
          ) : null}
        </div>

        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <SimpleTooltip
            title={t("marketing:favoritesCount", { count: listing.favorites })}
          >
            <Button variant="secondary" size="sm">
              <IconHeart className="size-4" />
              <span className="tabular-nums">{listing.favorites}</span>
            </Button>
          </SimpleTooltip>

          <SimpleTooltip
            title={t("marketing:onboardedCount", { count: listing.onboarded })}
          >
            <Button variant="secondary" size="sm">
              <IconUser className="size-4" />
              <span className="tabular-nums">{listing.onboarded}</span>
            </Button>
          </SimpleTooltip>

          {onShareLink ? (
            <SimpleTooltip title={t("marketing:copyMarketplaceLink")}>
              <Button
                variant="secondary"
                size="sm"
                aria-label={t("marketing:shareMarketplaceLink")}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  onShareLink();
                }}
              >
                <IconShare className="size-4" />
              </Button>
            </SimpleTooltip>
          ) : null}
        </div>
      </CardFooter>
    </Card>
  );
}

export default MarketplaceListingCard;
